import React from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose } from "react-icons/io5";
import { FiEye, FiEyeOff, FiTrash2, FiUploadCloud } from "react-icons/fi";
import { invalidate } from '@react-three/fiber';
import { observer } from 'mobx-react-lite';
import { editorStore } from '../../store/editorStore';
import { floorplannerStore } from '../../store/floorplannerStore';
import { BackdropImageType, isBackdropImageType } from '../../types/floorplannerTypes';
import { isBackdropVisible } from '../../utils/backdropImage';
import { DeleteFloorplanBackdropImageDocument } from '../../gql/floorplanBackdrop';
import { useFloorplansQuery } from '../../gql';

interface DropdownProps {
    position: { top: number; left: number };
    onClose: () => void;
    dropdownRef: React.Ref<HTMLDivElement>;
    onAddBackgroundImage: () => void;
}

export const UploadsPanel: React.FC<DropdownProps> = observer(({ position, onClose, dropdownRef, onAddBackgroundImage }) => {
    const { t } = useTranslation();
    const backdrops = Array.from(floorplannerStore.symbolsMap.values()).filter(isBackdropImageType);
    const { data } = useFloorplansQuery({ variables: {} });
    const currentUrls = new Set(backdrops.map((backdrop) => backdrop.backdropImagePath));
    const savedBackdrops = (data?.floorplans ?? [])
        .flatMap((floorplan) => {
            if (!floorplan) return [];
            const parsed: Array<{
                id: string;
                floorplanName: string;
                imageUrl: string;
                backdropRatio: number;
                backdropSize: number;
            }> = [];

            if (floorplan.backdropImage) {
                parsed.push({
                    id: `${floorplan.id}-root-${floorplan.backdropImage}`,
                    floorplanName: floorplan.name || t('common.untitled'),
                    imageUrl: floorplan.backdropImage,
                    backdropRatio: floorplan.backdropRatio ?? 1,
                    backdropSize: floorplan.backdropSize ?? 1000,
                });
            }

            try {
                const json = floorplan.json ? JSON.parse(floorplan.json) : null;
                const symbols = Array.isArray(json?.symbols) ? json.symbols : [];
                symbols.forEach((symbol: any) => {
                    if (symbol?.type === "backdropImage" && symbol.backdropImagePath) {
                        parsed.push({
                            id: `${floorplan.id}-${symbol.id}`,
                            floorplanName: floorplan.name || t('common.untitled'),
                            imageUrl: symbol.backdropImagePath,
                            backdropRatio: symbol.backdropRatio ?? 1,
                            backdropSize: symbol.backdropSize ?? 1000,
                        });
                    }
                });
            } catch {
                return parsed;
            }

            return parsed;
        })
        .filter((item, index, all) =>
            !currentUrls.has(item.imageUrl) &&
            all.findIndex((candidate) => candidate.imageUrl === item.imageUrl) === index
        );

    const selectBackdrop = (backdrop: BackdropImageType) => {
        editorStore.clearSelections();
        floorplannerStore.updateSymbolProperty(backdrop.id, "selected", true);
        editorStore.addSelection(backdrop);
        invalidate();
        onClose();
    };

    const toggleBackdropVisibility = (event: React.MouseEvent, backdrop: BackdropImageType) => {
        event.stopPropagation();
        if (floorplannerStore.lockedByOther) return;
        floorplannerStore.beginUndoTransaction('backdropVisibility');
        floorplannerStore.recordSymbolForUndo(backdrop.id);
        floorplannerStore.updateSymbolProperty(backdrop.id, "visible", !isBackdropVisible(backdrop));
        floorplannerStore.recordSymbolChange(backdrop.id, 'modify');
        floorplannerStore.pushToUndoStack();
        floorplannerStore.setDirty();
        invalidate();
    };

    const deleteBackdrop = async (event: React.MouseEvent, backdrop: BackdropImageType) => {
        event.stopPropagation();
        if (floorplannerStore.lockedByOther) return;
        const imageUrl = backdrop.backdropImagePath;
        const floorplanId = floorplannerStore.id;
        editorStore.removeSelection(backdrop);
        floorplannerStore.removeSymbol(backdrop.id);
        invalidate();

        if (!floorplanId || !imageUrl) return;

        try {
            await (floorplannerStore.client ?? editorStore.client)?.mutate({
                mutation: DeleteFloorplanBackdropImageDocument,
                variables: { floorplanId, imageUrl },
            });
        } catch (error) {
            console.warn("Could not delete floorplan backdrop image", error);
        }
    };

    const insertSavedBackdrop = (backdrop: typeof savedBackdrops[number]) => {
        const [centerX, centerY] = editorStore.getVisibleCenter();
        const id = floorplannerStore.addSymbol("backdropImage", [centerX, centerY], {
            backdropImagePath: backdrop.imageUrl,
            backdropRatio: backdrop.backdropRatio,
            backdropSize: backdrop.backdropSize,
            locked: false,
            zIndex: -1,
        });
        const symbol = floorplannerStore.symbolsMap.get(id);
        if (symbol) {
            editorStore.clearSelections();
            floorplannerStore.updateSymbolProperty(id, "selected", true);
            editorStore.addSelection(symbol);
        }
        invalidate();
        onClose();
    };

    return (
        <div
            className="fixed z-50 w-[398px] min-h-40 bg-white border border-gray-300 rounded shadow-md overflow-y-auto scrollbar"
            style={{ top: position.top, left: position.left, maxHeight: '70vh' }}
            ref={dropdownRef}
        >
            <div className="flex flex-cols items-center justify-between p-2">
                <div className="text-sm font-normal">{t('panels.uploads')}</div>
                <div onClick={onClose} className="text-sm text-gray-500"><IoClose style={{ strokeWidth: 0.5 }} size={24} /></div>
            </div>

            {/* Divider line */}
            <div className="border-b border-gray-300 w-full" />

            <div className={`p-3 space-y-3${floorplannerStore.lockedByOther ? ' opacity-50' : ''}`}>
                <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded border border-[#edebe6] bg-[#f7f6f0] p-3 text-left hover:bg-[#edebe6] disabled:cursor-not-allowed disabled:hover:bg-[#f7f6f0]"
                    onClick={onAddBackgroundImage}
                    disabled={floorplannerStore.lockedByOther}
                >
                    <span className="flex h-9 w-9 items-center justify-center rounded bg-white">
                        <FiUploadCloud size={18} />
                    </span>
                    <span className="flex flex-col">
                        <span className="text-sm font-medium text-black">{t('uploads.addBackgroundImage')}</span>
                        <span className="text-xs text-gray-500">{t('uploads.uploadAndSetRealScale')}</span>
                    </span>
                </button>

                {backdrops.length > 0 && (
                    <div>
                        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">{t('uploads.inThisFloorplan')}</div>
                        <p className="mb-2 text-[11px] leading-4 text-gray-500">{t('uploads.backgroundImagesHint')}</p>
                        <div className="grid grid-cols-3 gap-2">
                            {backdrops.map((backdrop, index) => {
                                const visible = isBackdropVisible(backdrop);
                                const statusLabel = !visible
                                    ? t('common.hidden')
                                    : backdrop.locked
                                      ? t('common.locked')
                                      : t('common.unlocked');
                                return (
                                <button
                                    key={backdrop.id}
                                    type="button"
                                    className={`group relative h-[86px] overflow-hidden rounded border bg-white text-left ${backdrop.selected ? "border-boniYellowPrimary ring-1 ring-boniYellowPrimary" : "border-[#edebe6]"}`}
                                    onClick={() => selectBackdrop(backdrop)}
                                    title={t('uploads.openBackgroundSettings', { index: index + 1 })}
                                >
                                    <img
                                        src={backdrop.backdropImagePath}
                                        alt={t('uploads.backgroundIndex', { index: index + 1 })}
                                        className={`h-full w-full object-cover ${visible ? '' : 'opacity-40'}`}
                                        crossOrigin="anonymous"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-[11px] text-white">
                                        {statusLabel}
                                    </div>
                                    {!floorplannerStore.lockedByOther && (
                                        <>
                                            <span
                                                className="absolute left-1 top-1 flex h-7 w-7 items-center justify-center rounded bg-white text-gray-700 shadow hover:bg-gray-50"
                                                onClick={(event) => toggleBackdropVisibility(event, backdrop)}
                                                title={visible ? t('uploads.hideBackgroundImage') : t('uploads.showBackgroundImage')}
                                                aria-label={visible ? t('uploads.hideBackgroundImage') : t('uploads.showBackgroundImage')}
                                            >
                                                {visible ? <FiEye size={14} /> : <FiEyeOff size={14} />}
                                            </span>
                                            <span
                                                className="absolute right-1 top-1 hidden h-7 w-7 items-center justify-center rounded bg-white text-red-600 shadow group-hover:flex"
                                                onClick={(event) => deleteBackdrop(event, backdrop)}
                                            >
                                                <FiTrash2 size={14} />
                                            </span>
                                        </>
                                    )}
                                </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {savedBackdrops.length > 0 && (
                    <div>
                        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">{t('uploads.previousBackgrounds')}</div>
                        <div className="grid grid-cols-3 gap-2">
                            {savedBackdrops.map((backdrop) => (
                                <button
                                    key={backdrop.id}
                                    type="button"
                                    className="group relative h-[86px] overflow-hidden rounded border border-[#edebe6] bg-white text-left hover:border-boniYellowPrimary disabled:cursor-not-allowed disabled:hover:border-[#edebe6]"
                                    onClick={() => insertSavedBackdrop(backdrop)}
                                    disabled={floorplannerStore.lockedByOther}
                                    title={t('uploads.useImageFrom', { name: backdrop.floorplanName })}
                                >
                                    <img
                                        src={backdrop.imageUrl}
                                        alt={t('uploads.backgroundFrom', { name: backdrop.floorplanName })}
                                        className="h-full w-full object-cover"
                                        crossOrigin="anonymous"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-2 py-1 text-[11px] text-white">
                                        {backdrop.floorplanName}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

        </div>

    );
});
